/**
 * Phase 11.3 → 18d — Inline spinner with named phase progression.
 *
 * Phase 18d honesty upgrade: instead of a single generic label, cycle
 * through the four canonical agent phases so users have a mental model
 * of where in the pipeline the run is. If a live signal is available
 * (`phase` prop passed by parent), we render that phase. Otherwise we
 * cycle every 3s with a tooltip explicitly labelling the estimate as
 * "no live signal" — no fake progress percentages.
 *
 * Props:
 *   active   — boolean; when false the component renders `null`.
 *   locale   — "de" | "en" (default "de").
 *   tip      — optional custom sub-text (overrides default).
 *   phase    — optional 0-3 index of the current phase. When passed,
 *              disables the auto-cycle and uses this as the live signal.
 *
 * data-testid: `agent-generation-spinner`, `agent-generation-spinner-phase`.
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const PHASES = {
  de: [
    "Briefing analysieren",
    "Strategie-Gedächtnis abrufen",
    "Text generieren",
    "Qualität bewerten",
  ],
  en: [
    "Analyzing brief",
    "Retrieving strategy",
    "Generating copy",
    "Judging quality",
  ],
};

const COPY = {
  de: { label: "KI arbeitet…", tip: "Kann bis zu 30 Sekunden dauern." },
  en: { label: "AI is thinking…", tip: "This may take up to 30 seconds." },
};

const HONEST_NO_SIGNAL_TITLE = {
  de: "Geschätzter Fortschritt — kein Live-Signal vom Backend.",
  en: "Estimated progress — no live signal from backend.",
};

export function AgentGenerationSpinner({ active, locale = "de", tip, phase }) {
  const lng = COPY[locale] ? locale : "de";
  const c = COPY[lng];
  const phases = PHASES[lng];
  const [cyclePhase, setCyclePhase] = useState(0);
  const liveSignal = typeof phase === "number";

  useEffect(() => {
    if (!active || liveSignal) return undefined;
    const t = setInterval(() => {
      setCyclePhase((p) => (p + 1) % phases.length);
    }, 3000);
    return () => clearInterval(t);
  }, [active, liveSignal, phases.length]);

  if (!active) return null;
  const currentPhase = liveSignal
    ? Math.max(0, Math.min(phases.length - 1, phase))
    : cyclePhase;

  return (
    <div
      data-testid="agent-generation-spinner"
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm"
    >
      <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
      <div className="flex flex-col leading-tight flex-1 min-w-0">
        <span data-testid="agent-generation-spinner-label"
              className="font-medium">{c.label}</span>
        <span
          data-testid="agent-generation-spinner-phase"
          className="text-xs text-primary/80 truncate"
          title={liveSignal ? undefined : HONEST_NO_SIGNAL_TITLE[lng]}
        >
          {phases[currentPhase]}
          {!liveSignal && (
            <span data-testid="agent-generation-spinner-heuristic-mark"
                    className="ml-1 text-muted-foreground">·</span>
          )}
        </span>
        <span data-testid="agent-generation-spinner-tip"
              className="text-xs text-muted-foreground">
          {tip || c.tip}
        </span>
      </div>
    </div>
  );
}

export default AgentGenerationSpinner;
